import {
  Component,
  OnDestroy,
  OnInit,
  HostBinding,
  HostListener,
  ViewEncapsulation,
} from "@angular/core";
import { Location } from "@angular/common";

import * as _ from "lodash";
import { Subject, Subscription } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { AuthenticationService } from "app/auth/service";
import { CoreSidebarService } from "@core/components/core-sidebar/core-sidebar.service";
import { CoreConfigService } from "@core/services/config.service";
import { CoreMediaService } from "@core/services/media.service";

import { User } from "app/auth/models";

import { coreConfig } from "app/app-config";
import { NavigationEnd, Router } from "@angular/router";
import { UserResponse } from "app/models/auth/user.model";
import { NavbarService } from "./navbar.service";
import { ConfigurationService } from "app/services/configuration/configuration.service";
import { BaseAppService } from "app/services/local-services/base-app-service.service";

@Component({
    selector: "app-navbar",
    templateUrl: "./navbar.component.html",
    styleUrls: ["./navbar.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class NavbarComponent implements OnInit, OnDestroy {
  public currentTimeInterval: any;
  public currentTime: Date;
  public horizontalMenu: boolean;
  public hiddenMenu: boolean;

  public coreConfig: any;
  public currentSkin: string;
  public prevSkin: string;

  public currentUser: User | null;
  public currentWorker: UserResponse | null;

  public isConfiguration: boolean = false;
  public isMaintenance: boolean = false;

  public languageOptions: any;
  public selectedLanguage: any;

  public urlMaintenance: string = "";

  public masterSlaveMode: boolean = false;

  public slaveMachine: boolean = false;

  @HostBinding("class.fixed-top")
  public isFixed = false;

  @HostBinding("class.navbar-static-style-on-scroll")
  public windowScrolled = false;

  // Add .navbar-static-style-on-scroll on scroll using HostListener & HostBinding
  @HostListener("window:scroll", [])
  onWindowScroll() {
    if (
      (window.pageYOffset ||
        document.documentElement.scrollTop ||
        document.body.scrollTop > 100) &&
      this.coreConfig.layout.navbar.type == "navbar-static-top" &&
      this.coreConfig.layout.type == "horizontal"
    ) {
      this.windowScrolled = true;
    } else if (
      (this.windowScrolled && window.pageYOffset) ||
      document.documentElement.scrollTop ||
      document.body.scrollTop < 10
    ) {
      this.windowScrolled = false;
    }
  }

  // Private
  private _unsubscribeAll: Subject<any>;
  private _subscriptions: Subscription[] = [];

  /**
   * Constructor
   *
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {CoreMediaService} _coreMediaService
   * @param {MediaObserver} _mediaObserver
   * @param {TranslateService} _translateService
   */
  constructor(
    private _router: Router,
    private _authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private _coreMediaService: CoreMediaService,
    private _coreSidebarService: CoreSidebarService,
    private navbarService: NavbarService,
    private _configurationService: ConfigurationService,
    private _baseAppService: BaseAppService,
    public _translateService: TranslateService,
    public location: Location
  ) {
    this.setCurrentTimeInterval();
    this._subscriptions.push(
      this._authenticationService.currentUser.subscribe((user) => {
        this.currentUser = user;
      })
    );
    this._subscriptions.push(
      this._authenticationService.currentWorker.subscribe((worker) => {
        this.currentWorker = worker;
        this.isMaintenance = this._authenticationService.isMaintenance;
        if (this.currentWorker && this.isMaintenance) {
          this.urlMaintenance = this.getMaintenanceUrl();
        }
      })
    );
    this._subscriptions.push(
      this._configurationService.masterSlaveObservable.subscribe(
        (masterSlaveMode) => {
          this.masterSlaveMode = masterSlaveMode;
        }
      )
    );
    this._subscriptions.push(
      this._baseAppService.slaveMachine.subscribe((slave) => {
        this.slaveMachine = slave;
      })
    );
    this.languageOptions = {
      en: {
        title: "English",
        flag: "us",
      },
      fr: {
        title: "French",
        flag: "fr",
      },
      es: {
        title: "Spanish",
        flag: "es",
      },
      ca: {
        title: "Catalan",
        flag: "es-ct",
      },
    };

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  setCurrentTimeInterval(): void {
    this.currentTime = new Date();
    clearInterval(this.currentTimeInterval);
    this.currentTimeInterval = setInterval(() => {
      this.currentTime = new Date();
    }, 1000);
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle sidebar open
   *
   * @param key
   */
  toggleSidebar(key): void {
    this._coreSidebarService.getSidebarRegistry(key)?.toggleOpen();
  }

  /**
   * Logout method
   */
  logout(event) {
    this.navbarService.logoutClickSubject.next(event);
    this._authenticationService.workerLogout();
    this._router.navigate(["/pages/login"]);
  }

  planifications(event) {
    this.navbarService.planificationClickSubject.next(event);
  }

  machines(event) {
    this.navbarService.machinesClickSubject.next(event);
  }

  configuration(event) {
    this.navbarService.configurationClickSubject.next(event);
  }

  goBack() {
    this.location.back();
  }

  getMaintenanceUrl(): string {
    let url = "";
    const baseUrl = "https://maintenance.endepro.";
    const extension = window.location.origin.split(".").pop();
    const route = "/#/pages/worker-code/";
    const params =
      this.currentWorker?.workerCode + "/" + this._translateService.currentLang;
    if (extension == "net" || extension == "local") {
      url = baseUrl + extension + route + params;
    } else {
      // url = baseUrl + "net" + route + params;
      url = "http://localhost:4270" + route + params;
    }
    return url;
  }

  listenRouter() {
    this.isConfiguration = this._router.url.includes("/configuration");
    this._subscriptions.push(
      this._router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event: any) => {
          this.isConfiguration = event && event.url.includes("/configuration");
        })
    );
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    // get the currentUser details from localStorage
    //this.currentUser = JSON.parse(localStorage.getItem('currentUser'));

    // Subscribe to the config changes
    this._coreConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.coreConfig = config;
        this.horizontalMenu = config.layout.type === "horizontal";
        this.hiddenMenu = config.layout.menu.hidden === true;
        this.currentSkin = config.layout.skin;
        // Fix: for vertical layout if default navbar fixed-top than set isFixed = true
        if (this.coreConfig.layout.type === "vertical") {
          setTimeout(() => {
            if (this.coreConfig.layout.navbar.type === "fixed-top") {
              this.isFixed = true;
            }
          }, 0);
        }
      });

    // Horizontal Layout Only: Add class fixed-top to navbar below large screen
    if (this.coreConfig.layout.type == "horizontal") {
      // On every media(screen) change
      this._coreMediaService.onMediaUpdate
        .pipe(takeUntil(this._unsubscribeAll))
        .subscribe(() => {
          const isFixedTop = this.coreConfig.isActive("bs-gt-xl");
          if (isFixedTop) {
            this.isFixed = false;
          } else {
            this.isFixed = true;
          }
        });
    }

    // Set the selected language from default languageOptions
    this.selectedLanguage = _.find(this.languageOptions, {
      id: this._translateService.currentLang,
    });

    this.listenRouter();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    clearInterval(this.currentTimeInterval);
    this._subscriptions.forEach((x) => x.unsubscribe());
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
